import { useEffect, useState } from 'react';
import { CheckCircle2, MailOpen, Mail, UserPlus, X, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { clientsAPI } from '../../lib/clientsAPI';
import { commsAPI, errMsg, notifyCommsChanged } from '../../lib/communicationsAPI';

// Minimal bulk actions: assign, mark done, mark read/unread.
export const BulkBar = ({ ids, staff, onDone, onClear }) => {
  const [busy, setBusy] = useState(false);
  const run = async (action, extra = {}) => {
    setBusy(true);
    try {
      const res = await commsAPI.bulk({ ids, action, ...extra });
      toast.success(`${res.data.updated.length} conversation(s) updated`);
      notifyCommsChanged(); onDone();
    } catch (err) { toast.error(errMsg(err, 'Bulk action failed')); } finally { setBusy(false); }
  };
  const btn = (action, Icon, label) => (
    <Button size="sm" variant="ghost" className="h-8 px-2" disabled={busy} onClick={() => run(action)} title={label} aria-label={label} data-testid={`comms-bulk-${action}`}><Icon className="w-4 h-4" /></Button>
  );
  return (
    <div className="flex items-center gap-1 rounded-md bg-accent/60 px-2 py-1" data-testid="comms-bulk-bar">
      <span className="text-xs font-medium mr-1" data-testid="comms-bulk-count">{ids.length} selected</span>
      {btn('done', CheckCircle2, 'Mark done')}
      {btn('mark_read', MailOpen, 'Mark read')}
      {btn('mark_unread', Mail, 'Mark unread')}
      <Select onValueChange={(v) => run('assign', { assigned_user_id: v === 'none' ? null : v })} disabled={busy}>
        <SelectTrigger className="h-8 w-8 p-0 justify-center border-0 bg-transparent [&>svg:last-child]:hidden" aria-label="Assign" data-testid="comms-bulk-assign"><UserPlus className="w-4 h-4" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Unassigned</SelectItem>
          {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name || s.email}</SelectItem>)}
        </SelectContent>
      </Select>
      <Button size="sm" variant="ghost" className="h-8 px-2 ml-auto" onClick={onClear} aria-label="Clear selection" data-testid="comms-bulk-clear"><X className="w-4 h-4" /></Button>
    </div>
  );
};

const FIELD_LABEL = { channel: 'Channel', client_id: 'Client', assigned_user_id: 'Assignee', date_from: 'From', date_to: 'To', urgent: 'Urgent only', has_attachments: 'Has attachments', unread: 'Unread only' };

// One "Filter" popover: channel, client, assignee, date range, urgent only, has attachments.
export const FilterPopover = ({ filters, onChange, staff }) => {
  const [clients, setClients] = useState([]);
  useEffect(() => { clientsAPI.list().then((r) => setClients(r.data || [])).catch(() => {}); }, []);
  const set = (k, v) => onChange({ ...filters, [k]: v });
  const active = Object.values(filters).filter((v) => v !== '' && v !== false && v != null).length;
  const sel = (k, placeholder, opts) => (
    <Select value={filters[k] || 'any'} onValueChange={(v) => set(k, v === 'any' ? '' : v)}>
      <SelectTrigger className="h-9" data-testid={`comms-filter-${k}`}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent><SelectItem value="any">{placeholder}</SelectItem>{opts.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="h-8" data-testid="comms-filter-btn"><SlidersHorizontal className="w-3.5 h-3.5 mr-1.5" />Filter{active ? ` (${active})` : ''}</Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 space-y-3" data-testid="comms-filter-popover">
        <div className="space-y-1"><Label className="text-xs">Channel</Label>{sel('channel', 'All channels', [['portal', 'Client portal'], ['email', 'Email']])}</div>
        <div className="space-y-1"><Label className="text-xs">Client</Label>{sel('client_id', 'All clients', clients.map((c) => [c.id, c.display_name]))}</div>
        <div className="space-y-1"><Label className="text-xs">Assignee</Label>{sel('assigned_user_id', 'Anyone', [['none', 'Unassigned'], ...staff.map((s) => [s.id, s.name || s.email])])}</div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1"><Label className="text-xs">From</Label><Input type="date" className="h-9" value={filters.date_from || ''} onChange={(e) => set('date_from', e.target.value)} data-testid="comms-filter-date_from" /></div>
          <div className="space-y-1"><Label className="text-xs">To</Label><Input type="date" className="h-9" value={filters.date_to || ''} onChange={(e) => set('date_to', e.target.value)} data-testid="comms-filter-date_to" /></div>
        </div>
        <div className="flex items-center justify-between"><Label className="text-sm">Urgent only</Label><Switch checked={!!filters.urgent} onCheckedChange={(v) => set('urgent', v)} data-testid="comms-filter-urgent" /></div>
        <div className="flex items-center justify-between"><Label className="text-sm">Has attachments</Label><Switch checked={!!filters.has_attachments} onCheckedChange={(v) => set('has_attachments', v)} data-testid="comms-filter-has_attachments" /></div>
        <Button size="sm" variant="ghost" className="w-full" onClick={() => onChange({})} data-testid="comms-filter-clear-all">Clear all</Button>
      </PopoverContent>
    </Popover>
  );
};

export const FilterChips = ({ filters, onChange, staff }) => {
  const entries = Object.entries(filters).filter(([, v]) => v !== '' && v !== false && v != null);
  if (!entries.length) return null;
  const show = (k, v) => {
    if (k === 'assigned_user_id') return v === 'none' ? 'Unassigned' : (staff.find((s) => s.id === v)?.name || 'Assignee');
    if (k === 'client_id') return 'Client';
    if (v === true) return FIELD_LABEL[k];
    return `${FIELD_LABEL[k]}: ${v}`;
  };
  return (
    <div className="flex flex-wrap gap-1" data-testid="comms-filter-chips">
      {entries.map(([k, v]) => (
        <span key={k} className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[11px]" data-testid={`comms-filter-chip-${k}`}>
          {show(k, v)}<button type="button" onClick={() => onChange({ ...filters, [k]: '' })} aria-label={`Clear ${FIELD_LABEL[k]}`}><X className="w-3 h-3" /></button>
        </span>
      ))}
    </div>
  );
};
